import React, { useEffect, useState, useRef } from "react";
import Popover from "@mui/material/Popover";
import { Dropdown } from "react-bootstrap";

const CustomMultiBarChart = (props) => {
  const [monthBasedReceivables, setMonthBasedReceivables] = useState(undefined);
  const [selectedReceivables, setSelectedReceivables] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverContent, setPopoverContent] = useState("");
  const [tooltipInfo, setTooltipInfo] = useState({ visible: false, x: 0, y: 0, content: {} });
  const [hiddenDatasets, setHiddenDatasets] = useState(new Set());
  const chartContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        setContainerWidth(chartContainerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    fetchSuitableReceivable();
  }, [props.val]);

  const fetchSuitableReceivable = () => {
    const groupedData = props.val.reduce((acc, obj) => {
      const key = `${obj.flg_chart_legend_x_filter}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});

    if (groupedData) {
      const filteredGroupedData = Object.values(groupedData);
      setMonthBasedReceivables(filteredGroupedData);
      setSelectedReceivables(filteredGroupedData[0]);
    }
  };

  const handlePopoverOpen = (event, content) => {
    setAnchorEl(event.currentTarget);
    setPopoverContent(content);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleLegendClick = (index) => {
    setHiddenDatasets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const open = Boolean(anchorEl);

  const groupedData = selectedReceivables?.reduce((acc, obj) => {
    const key = `${obj.chart_x_value}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});

  const datasets = Object.values(groupedData || {});
  const labels = selectedReceivables?.map(val => val.chart_legend_y_title);
  const uniqueLabels = [...new Set(labels || [])];

  const colorArray = [
    [75, 192, 192],
    [153, 102, 255],
    [102, 253, 255],
    [95, 192, 212],
    [133, 82, 235]
  ];

  const allValues = selectedReceivables?.map(item => item.chart_y_value) || [];
  const maxValue = Math.max(...allValues, 1);

  const calculateChartDimensions = () => {
    const availableWidth = containerWidth - 80;
    const numGroups = uniqueLabels.length;
    const visibleDatasetsCount = datasets.length - hiddenDatasets.size;

    // Calculate minimum spacing needed
    const minGroupSpacing = 40; 
    const minBarWidth = 15;

    // Calculate total width needed
    const totalGroupSpacing = (numGroups - 1) * minGroupSpacing;
    const availableForBars = availableWidth - totalGroupSpacing;

    // Calculate bar width that will fit
    const barWidth = Math.max(
      minBarWidth,
      (availableForBars / (numGroups * visibleDatasetsCount))
    );

    return {
      barWidth,
      groupSpacing: minGroupSpacing,
      chartHeight: 250,
      legendHeight: 50,
      chartPadding: 70,
      xAxisHeight: 60 // Added for better x-axis label spacing
    };
  };


  const dimensions = calculateChartDimensions();

  const handleBarHover = (event, data) => {
    if (!chartContainerRef.current) return;

    const rect = chartContainerRef.current.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    setTooltipInfo({
      visible: true,
      x: offsetX,
      y: offsetY,
      content: data
    });
  };

  const handleBarLeave = () => {
    setTooltipInfo({ visible: false, x: 0, y: 0, content: {} });
  };

  const yAxisPoints = Array.from({ length: 6 }, (_, i) => i / 5);

  return (
    <div className="w-100">
      {/* Header section */}
      <div className="row d-flex justify-content-between align-items-center my-3">
        <h3 className="dashboard__container-title m-0">
          {props.val[0]?.chart_title}
        </h3>
        <div>
          <Dropdown>
            <Dropdown.Toggle
              variant="primary"
              style={{
                width: "200px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#fff",
                border: "1px solid #E4E6EF",
                color: "var(--secondary-text-color)",
                fontWeight: "400",
                fontSize: "10px",
              }}
            >
              <span style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "calc(100% - 20px)",
              }}>
                {selectedReceivables && selectedReceivables[0]?.flg_chart_legend_x_filter}
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ minWidth: '200px', width: 'max-content' }}>
              {monthBasedReceivables && monthBasedReceivables.map((obj, index) => (
                <Dropdown.Item
                  key={index}
                  onClick={() => setSelectedReceivables(obj)}
                >
                  {obj[0].flg_chart_legend_x_filter}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Legends section */}
      <div style={{ marginBottom: "10px" }}>
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          padding: "8px"
        }}>
          {datasets.map((dataset, index) => {
            const barColor = colorArray[index % colorArray.length];
            const isHidden = hiddenDatasets.has(index);

            return (
              <div
                key={index}
                onClick={() => handleLegendClick(index)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: "120px",
                  maxWidth: "200px",
                  cursor: "pointer",
                  opacity: isHidden ? 0.5 : 1,
                }}
              >
                <div style={{
                  width: "15px",
                  height: "15px",
                  marginRight: "8px",
                  backgroundColor: `rgba(${barColor[0]}, ${barColor[1]}, ${barColor[2]}, 0.2)`,
                  border: `2px solid rgba(${barColor[0]}, ${barColor[1]}, ${barColor[2]}, 1)`,
                  boxShadow: isHidden ? 'inset 0 0 0 2px #fff' : 'none'
                }} />
                <span style={{
                  fontSize: "12px",
                  color: "#666",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}>
                  {dataset[0].chart_x_value || `Data Series ${index + 1}`}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chart section */}
      <div
        ref={chartContainerRef}
        style={{
          height: dimensions.chartHeight + dimensions.legendHeight + dimensions.chartPadding * 2,
          width: "100%",
          position: "relative"
        }}
      >
        <svg width="100%" height="120%">
          <g transform={`translate(${dimensions.chartPadding}, ${dimensions.legendHeight + dimensions.chartPadding})`}>
            {/* Axes */}
            <line
              x1="0"
              y1="0"
              x2="0"
              y2={dimensions.chartHeight}
              stroke="#ccc"
              strokeWidth="1"
            />
            <line
              x1="0"
              y1={dimensions.chartHeight}
              x2={containerWidth - dimensions.chartPadding * 2}
              y2={dimensions.chartHeight}
              stroke="#ccc"
              strokeWidth="1"
            />

            {/* Y-axis labels and grid lines */}
            {yAxisPoints.map((ratio, index) => {
              const yPos = dimensions.chartHeight - (ratio * dimensions.chartHeight);
              const value = Math.round(maxValue * ratio);
              return (
                <g key={index}>
                  <line
                    x1="-5"
                    y1={yPos}
                    x2={containerWidth - dimensions.chartPadding * 2}
                    y2={yPos}
                    stroke="#eee"
                    strokeWidth="1"
                  />
                  <text
                    x="-10"
                    y={yPos + 5}
                    textAnchor="end"
                    fontSize="12"
                    fill="#666"
                  >
                    {value}
                  </text>
                </g>
              );
            })}

            {/* Bars and X-axis labels */}
            {uniqueLabels.map((label, labelIndex) => {
              // Calculate visible bars for this label
              const visibleBars = datasets.filter((dataset, index) => {
                const hasData = dataset.find(item => item.chart_legend_y_title === label);
                return !hiddenDatasets.has(index) && hasData;
              });

              // Skip if no visible bars
              if (visibleBars.length === 0) return null;

              // Calculate group positioning
              const totalVisibleGroups = uniqueLabels.filter(l => {
                const hasVisibleBars = datasets.some((dataset, index) => {
                  const hasData = dataset.find(item => item.chart_legend_y_title === l);
                  return !hiddenDatasets.has(index) && hasData;
                });
                return hasVisibleBars;
              }).length;

              const visibleGroupIndex = uniqueLabels
                .slice(0, labelIndex)
                .filter(l => {
                  const hasVisibleBars = datasets.some((dataset, index) => {
                    const hasData = dataset.find(item => item.chart_legend_y_title === l);
                    return !hiddenDatasets.has(index) && hasData;
                  });
                  return hasVisibleBars;
                }).length;

              const availableWidth = containerWidth - (dimensions.chartPadding * 2);
              const groupWidth = (availableWidth / totalVisibleGroups);
              const groupStartX = visibleGroupIndex * groupWidth;
              const groupCenterX = groupStartX + (groupWidth / 2);

              return (
                <g key={labelIndex}>
                  {/* X-axis label */}
                  <g transform={`translate(${groupCenterX}, ${dimensions.chartHeight + 10})`}>
                    <text
                      textAnchor="middle"
                      fontSize="12"
                      fill="#666"
                      transform="rotate(-45)"
                      onMouseEnter={(e) => handlePopoverOpen(e, label)}
                      onMouseLeave={handlePopoverClose}
                    >
                      {label.length > 10 ? `${label.substring(0, 10)}...` : label}
                    </text>
                  </g>

                  {/* Bars */}
                  {datasets.map((dataset, datasetIndex) => {
                    if (hiddenDatasets.has(datasetIndex)) return null;

                    const barData = dataset.find(item => item.chart_legend_y_title === label);
                    if (!barData) return null;

                    const visibleIndex = Array.from(datasets.keys())
                      .filter(i => !hiddenDatasets.has(i) && i <= datasetIndex)
                      .length - 1;

                    const barsInGroup = visibleBars.length;
                    const barWidth = (groupWidth - dimensions.groupSpacing) / barsInGroup;
                    const barX = groupStartX + (visibleIndex * barWidth) + (dimensions.groupSpacing / 2);
                    const value = barData.chart_y_value;
                    const barHeight = (value / maxValue) * dimensions.chartHeight;
                    const barY = dimensions.chartHeight - barHeight;
                    const barColor = colorArray[datasetIndex % colorArray.length];

                    return (
                      <rect
                        key={datasetIndex}
                        x={barX}
                        y={barY}
                        width={Math.max(barWidth - 4, 5)}
                        height={barHeight}
                        fill={`rgba(${barColor[0]}, ${barColor[1]}, ${barColor[2]}, 0.2)`}
                        stroke={`rgba(${barColor[0]}, ${barColor[1]}, ${barColor[2]}, 1)`}
                        strokeWidth="1"
                        onMouseEnter={(e) => handleBarHover(e, barData)}
                        onMouseLeave={handleBarLeave}
                        style={{ cursor: "pointer" }}
                      />
                    );
                  })}
                </g>
              );
            })}
          </g>
        </svg>

        {/* Tooltip */}
        {tooltipInfo.visible && (
          <div style={{
            position: "absolute",
            top: tooltipInfo.y - 80,
            left: tooltipInfo.x + 10,
            backgroundColor: "rgba(198, 198, 198, 0.95)",
            border: "1px solid #ddd",
            borderRadius: "4px",
            padding: "8px 12px",
            boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
            zIndex: 1000,
            pointerEvents: "none",
            minWidth: "150px",
            fontSize: "12px"
          }}>
            <div style={{ borderBottom: "1px solid #eee", marginBottom: "5px", paddingBottom: "5px", fontWeight: "bold" }}>
              {tooltipInfo.content.chart_legend_y_title}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
              <span>{tooltipInfo.content.chart_x_value}:</span>
              <strong>{tooltipInfo.content.chart_y_value}</strong>
            </div>
            {tooltipInfo.content.flg_chart_legend_x_filter && (
              <div style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}>
                Period: {tooltipInfo.content.flg_chart_legend_x_filter}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          pointerEvents: 'none',
        }}
      >
        <div style={{ padding: "10px", maxWidth: "250px" }}>
          <p style={{ margin: "0 0 5px 0", fontSize: "14px" }}>{popoverContent}</p>
        </div>
      </Popover>
    </div>
  );
};

export default CustomMultiBarChart;